// FASE 2.6 — Brief IA widget. Público, poll /api/shadow/ai/daily-brief cada 10 min.
// V2 (2026-07): añadido selector de slots anteriores usando /history.
import React, { useCallback, useMemo, useState } from "react";
import { Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "../../lib/api";
import { useLanguage } from "../../i18n/LanguageContext";
import { tFases } from "../../i18n/shadow-fases";
import { useSlotBoundary } from "../../hooks/useSlotBoundary";

const NAV_LABEL = {
  now: { es: "Ahora", en: "Now", sk: "Teraz", de: "Jetzt", "zh-CN": "现在" },
  prev: { es: "Anterior", en: "Previous", sk: "Predchádzajúci", de: "Zurück", "zh-CN": "上一个" },
  next: { es: "Siguiente", en: "Next", sk: "Ďalší", de: "Weiter", "zh-CN": "下一步" },
  sparkTitle: { es: "Sentimiento por slot (más reciente →)", en: "Sentiment per slot (latest →)", sk: "Sentiment na slot (najnovší →)", de: "Stimmung pro Slot (neueste →)", "zh-CN": "按时段情绪（最新 →）" },
  slotEmpty: { es: "sin datos", en: "no data", sk: "žiadne dáta", de: "keine Daten", "zh-CN": "无数据" },
};

// ─── Sentiment sparkline ─────────────────────────────────────────────
// Recibe `items` (más nuevo primero) — la dibuja de izq(oldest) a der(latest)
// para que la lectura sea temporal natural. Color por dominante:
//   LONG > SHORT → verde, SHORT > LONG → rojo, empate/vacío → gris.
// Altura proporcional a analyses_count con floor visible (5px) para slots vacíos.
function SentimentSparkline({ items, activeIdx, onSelect, lang }) {
  const ordered = useMemo(() => [...items].reverse(), [items]); // oldest → newest
  const maxCount = useMemo(
    () => Math.max(1, ...items.map((b) => Number(b?.analyses_count) || 0)),
    [items],
  );
  if (ordered.length < 2) return null;

  return (
    <div
      className="mt-3 flex items-end gap-1"
      data-testid="daily-brief-sparkline"
      role="group"
      aria-label={NAV_LABEL.sparkTitle[lang] || NAV_LABEL.sparkTitle.es}
      title={NAV_LABEL.sparkTitle[lang] || NAV_LABEL.sparkTitle.es}
      style={{ height: 34 }}
    >
      {ordered.map((b, i) => {
        const s = b?.sentiment || {};
        const longN = Number(s.LONG || 0);
        const shortN = Number(s.SHORT || 0);
        const count = Number(b?.analyses_count || 0);
        // dominante
        let color = "rgba(255,255,255,0.15)";
        if (count > 0) {
          if (longN > shortN) color = "#00d084";
          else if (shortN > longN) color = "#ff3b5c";
          else color = "rgba(180,180,190,0.4)";
        }
        const h = count > 0 ? 8 + Math.round((count / maxCount) * 22) : 5;
        // El array `items` está más-nuevo-primero → activeIdx apunta a items[activeIdx].
        // En `ordered` (reversed) el activo es al reverse-index.
        const originalIdx = ordered.length - 1 - i;
        const isActive = originalIdx === activeIdx;
        return (
          <button
            key={b?.slot_key || b?.date || i}
            type="button"
            onClick={() => onSelect(originalIdx)}
            data-testid={`daily-brief-spark-${originalIdx}`}
            aria-label={`slot ${b?.slot_key || b?.date || i} · ${count} ${count === 1 ? "post" : "posts"}`}
            title={
              count > 0
                ? `${(b?.slot_key || "").slice(11, 16) || b?.date || ""} · ${count} posts · L${longN}/S${shortN}`
                : `${(b?.slot_key || "").slice(11, 16) || b?.date || ""} · ${NAV_LABEL.slotEmpty[lang] || NAV_LABEL.slotEmpty.es}`
            }
            className={`flex-1 rounded-t-sm transition-all ${isActive ? "ring-1 ring-violet-300/70 ring-offset-1 ring-offset-transparent scale-105" : "opacity-70 hover:opacity-100"}`}
            style={{ height: h, background: color, minWidth: 8 }}
          />
        );
      })}
    </div>
  );
}

export const DailyBriefWidget = () => {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);        // últimos N briefs (desc)
  const [idx, setIdx] = useState(0);              // 0 = más nuevo
  const activeLang = lang || "es";

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/shadow/ai/daily-brief/history?lang=${encodeURIComponent(activeLang)}&limit=6`);
      const j = await r.json();
      const arr = Array.isArray(j?.items) ? j.items : [];
      setItems(arr);
      // No resetear el idx si estamos navegando a un histórico — solo si el idx queda fuera de rango.
      setIdx((i) => (i >= arr.length ? 0 : i));
    } catch (_e) { /* non-critical */ }
  }, [activeLang]);

  // Refresh sincronizado a slot boundaries (00/04/08/12/16/20 UTC + 30s)
  useSlotBoundary(load);

  const brief = useMemo(() => items[idx] || null, [items, idx]);
  if (!brief) return null;

  const hasMultiple = items.length > 1;
  const isLatest = idx === 0;

  return (
    <div
      data-testid="daily-brief-widget"
      className="rounded-xl border border-violet-400/20 bg-gradient-to-br from-violet-500/[0.06] to-fuchsia-500/[0.04] p-4 backdrop-blur"
    >
      <div className="mb-2 flex items-center gap-2 flex-wrap">
        <Sparkles className="h-3.5 w-3.5 text-violet-300" />
        <span className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-violet-200">
          {tFases(activeLang, "db_title")}
          {brief.slot_key
            ? ` · ${new Date(brief.slot_key).toISOString().slice(11, 16)} UTC`
            : brief.date ? ` · ${brief.date}` : ""}
        </span>
        {brief.window_hours && (
          <span
            className="rounded-full border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[9px] text-white/50"
            title="Ventana de posts analizada"
          >
            {brief.window_hours}h
          </span>
        )}
        {isLatest && hasMultiple && (
          <span className="rounded-full bg-violet-400/15 px-1.5 py-0.5 font-mono text-[9px] font-bold text-violet-200">
            {NAV_LABEL.now[activeLang] || NAV_LABEL.now.es}
          </span>
        )}

        {hasMultiple && (
          <div className="ml-auto flex items-center gap-1" data-testid="daily-brief-nav">
            <button
              type="button"
              onClick={() => setIdx((i) => Math.min(items.length - 1, i + 1))}
              disabled={idx >= items.length - 1}
              data-testid="daily-brief-prev"
              title={NAV_LABEL.prev[activeLang] || NAV_LABEL.prev.es}
              aria-label={NAV_LABEL.prev[activeLang] || NAV_LABEL.prev.es}
              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-white/60 transition-colors hover:border-violet-400/40 hover:bg-violet-400/10 hover:text-violet-200 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="font-mono text-[9px] tabular-nums text-white/45">
              {idx + 1}/{items.length}
            </span>
            <button
              type="button"
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx <= 0}
              data-testid="daily-brief-next"
              title={NAV_LABEL.next[activeLang] || NAV_LABEL.next.es}
              aria-label={NAV_LABEL.next[activeLang] || NAV_LABEL.next.es}
              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-white/60 transition-colors hover:border-violet-400/40 hover:bg-violet-400/10 hover:text-violet-200 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      <p className="text-[12px] font-bold leading-snug text-white" data-testid="daily-brief-headline">
        {brief.headline}
      </p>
      <p className="mt-1.5 text-[11px] leading-relaxed text-white/70">{brief.summary}</p>
      {brief.key_takeaway && (
        <p className="mt-2 rounded-lg border border-amber-400/20 bg-amber-400/[0.06] px-2.5 py-1.5 text-[11px] text-amber-200" data-testid="daily-brief-takeaway">
          💡 {brief.key_takeaway}
        </p>
      )}
      {Array.isArray(brief.tickers) && brief.tickers.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {brief.tickers.map((t) => (
            <span key={t} className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 font-mono text-[9px] font-bold text-white/65">
              {t}
            </span>
          ))}
        </div>
      )}

      <SentimentSparkline items={items} activeIdx={idx} onSelect={setIdx} lang={activeLang} />
    </div>
  );
};

export default DailyBriefWidget;
